/**
 * Family Members Hook
 * Feature: 004-restore-missing-features
 * Task: T006
 *
 * Provides CRUD operations for family members linked to patients
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { getApiBaseUrl } from '@/lib/api-url'
import { cleanPhoneNumber, buildWhatsappNumber } from '@/features/shared/utils/phoneUtils'
import type {
  FamilyMember,
  CreateFamilyMemberInput,
  UpdateFamilyMemberInput,
  RelationshipType,
} from '@/features/shared/types/patient.types'

interface FamilyMemberMutationState {
  isCreating: boolean
  isUpdating: boolean
  isDeleting: boolean
  isSettingPrimary: boolean
}

const API_BASE_URL = getApiBaseUrl()

async function sendFamilyOnboardingIfEligible(member: FamilyMember): Promise<void> {
  if (
    !member.is_primary_contact ||
    !member.accepts_notifications ||
    !member.whatsapp_number
  ) {
    return
  }

  try {
    const {
      data: { session },
    } = await supabase.auth.getSession()

    if (!session?.access_token) return

    const response = await fetch(
      `${API_BASE_URL}/api/whatsapp/family-members/${member.id}/send-onboarding`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      },
    )

    if (!response.ok) {
      console.warn('Failed to trigger family WhatsApp onboarding', {
        familyMemberId: member.id,
        status: response.status,
      })
    }
  } catch (error) {
    console.warn('Failed to trigger family WhatsApp onboarding', {
      familyMemberId: member.id,
      error,
    })
  }
}

/**
 * Hook for managing family members of a patient
 * Provides create, update, delete, and setPrimaryContact operations
 */
export function useFamilyMembers(patientId?: string) {
  const queryClient = useQueryClient()

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['patient', patientId] })
    queryClient.invalidateQueries({ queryKey: ['patients'] })
    queryClient.invalidateQueries({ queryKey: ['patient-detail', patientId] })
    queryClient.invalidateQueries({ queryKey: ['patient-family-members', patientId] })
  }

  // Create family member mutation
  const createMutation = useMutation({
    mutationFn: async (input: CreateFamilyMemberInput): Promise<FamilyMember> => {
      // First, create or find the profile for this family member
      let profileId: string

      // Only look up by email when one is provided (trim to avoid empty string bug)
      const trimmedEmail = input.email?.trim()
      if (trimmedEmail) {
        const { data: existingProfile, error: profileLookupError } = await supabase
          .from('profiles')
          .select('id')
          .eq('email', trimmedEmail)
          .maybeSingle()

        if (profileLookupError) throw profileLookupError

        if (existingProfile) {
          profileId = existingProfile.id
        } else {
          // Create a new profile with email
          const { data: newProfile, error: profileError } = await supabase
            .from('profiles')
            .insert({
              email: trimmedEmail,
              full_name: input.name,
              phone: input.phone,
              country_code: input.country_code,
            })
            .select('id')
            .single()

          if (profileError) throw profileError
          profileId = newProfile.id
        }
      } else {
        // No email provided — create a profile without email
        const { data: newProfile, error: profileError } = await supabase
          .from('profiles')
          .insert({
            full_name: input.name,
            phone: input.phone,
            country_code: input.country_code,
          })
          .select('id')
          .single()

        if (profileError) throw profileError
        profileId = newProfile.id
      }

      // If this member should be primary, first unset any existing primary contact
      if (input.is_primary_contact) {
        await supabase
          .from('family_members')
          .update({ is_primary_contact: false })
          .eq('patient_id', input.patient_id)
      }

      // Create the family member record
      // Store phone as digits only with DDI prefix for WhatsApp API compatibility
      const { data, error } = await supabase
        .from('family_members')
        .insert({
          patient_id: input.patient_id,
          profile_id: profileId,
          relationship: input.relationship,
          is_primary_contact: input.is_primary_contact,
          accepts_notifications: input.accepts_notifications ?? false,
          whatsapp_number: input.phone
            ? buildWhatsappNumber(input.country_code, cleanPhoneNumber(input.phone))
            : null,
          country_code: input.country_code,
          can_report: true,
        })
        .select(`
          *,
          profiles (
            id,
            full_name,
            email,
            phone
          )
        `)
        .single()

      if (error) throw error
      await sendFamilyOnboardingIfEligible(data as FamilyMember)
      return data as FamilyMember
    },
    onSuccess: () => {
      invalidateQueries()
    },
  })

  // Update family member mutation
  const updateMutation = useMutation({
    mutationFn: async (input: UpdateFamilyMemberInput): Promise<FamilyMember> => {
      // Get the current family member to find profile_id
      const { data: currentMember, error: fetchError } = await supabase
        .from('family_members')
        .select('profile_id, profiles(auth_user_id, email)')
        .eq('id', input.id)
        .single()

      if (fetchError) throw fetchError

      const linkedProfile = Array.isArray(currentMember.profiles)
        ? currentMember.profiles[0]
        : currentMember.profiles

      if (
        input.email &&
        linkedProfile?.auth_user_id &&
        input.email.toLowerCase() !== linkedProfile.email?.toLowerCase()
      ) {
        throw new Error(
          'Email de usuario com conta de acesso deve ser alterado no painel de usuarios.',
        )
      }

      // Update profile if name, email, phone, or country_code changed
      if (input.name || input.email || input.phone || input.country_code) {
        const profileUpdate: Record<string, string> = {}
        if (input.name) profileUpdate.full_name = input.name
        if (input.email) profileUpdate.email = input.email
        if (input.phone) profileUpdate.phone = input.phone
        if (input.country_code) profileUpdate.country_code = input.country_code

        const { error: profileError } = await supabase
          .from('profiles')
          .update(profileUpdate)
          .eq('id', currentMember.profile_id)

        if (profileError) throw profileError
      }

      // Update family member record
      const familyMemberUpdate: Record<string, unknown> = {}
      if (input.relationship) familyMemberUpdate.relationship = input.relationship
      if (input.is_primary_contact !== undefined) {
        familyMemberUpdate.is_primary_contact = input.is_primary_contact
      }
      if (input.accepts_notifications !== undefined) {
        familyMemberUpdate.accepts_notifications = input.accepts_notifications
      }
      if (input.country_code) familyMemberUpdate.country_code = input.country_code
      if (input.phone) {
        const countryCode = input.country_code ?? '55'
        familyMemberUpdate.whatsapp_number = buildWhatsappNumber(countryCode, cleanPhoneNumber(input.phone))
      }

      const { data, error } = await supabase
        .from('family_members')
        .update(familyMemberUpdate)
        .eq('id', input.id)
        .select(`
          *,
          profiles (
            id,
            full_name,
            email,
            phone
          )
        `)
        .single()

      if (error) throw error
      await sendFamilyOnboardingIfEligible(data as FamilyMember)
      return data as FamilyMember
    },
    onSuccess: () => {
      invalidateQueries()
    },
  })

  // Delete family member mutation
  const deleteMutation = useMutation({
    mutationFn: async (memberId: string): Promise<void> => {
      const { error } = await supabase
        .from('family_members')
        .delete()
        .eq('id', memberId)

      if (error) throw error
    },
    onSuccess: () => {
      invalidateQueries()
    },
  })

  // Set primary contact mutation
  const setPrimaryMutation = useMutation({
    mutationFn: async ({
      memberId,
      patientId,
    }: {
      memberId: string
      patientId: string
    }): Promise<void> => {
      // First, unset all primary contacts for this patient
      const { error: unsetError } = await supabase
        .from('family_members')
        .update({ is_primary_contact: false })
        .eq('patient_id', patientId)

      if (unsetError) throw unsetError

      // Then set the specified member as primary
      const { data, error: setError } = await supabase
        .from('family_members')
        .update({ is_primary_contact: true })
        .eq('id', memberId)
        .select(`
          *,
          profiles (
            id,
            full_name,
            email,
            phone
          )
        `)
        .single()

      if (setError) throw setError
      if (data) await sendFamilyOnboardingIfEligible(data as FamilyMember)
    },
    onSuccess: () => {
      invalidateQueries()
    },
  })

  // Convenience wrapper functions
  const createMember = async (data: CreateFamilyMemberInput): Promise<FamilyMember> => {
    return createMutation.mutateAsync(data)
  }

  const updateMember = async (data: UpdateFamilyMemberInput): Promise<FamilyMember> => {
    return updateMutation.mutateAsync(data)
  }

  const deleteMember = async (memberId: string): Promise<void> => {
    return deleteMutation.mutateAsync(memberId)
  }

  const setPrimaryContact = async (memberId: string, targetPatientId: string): Promise<void> => {
    return setPrimaryMutation.mutateAsync({ memberId, patientId: targetPatientId })
  }

  const state: FamilyMemberMutationState = {
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    isSettingPrimary: setPrimaryMutation.isPending,
  }

  return {
    createMember,
    updateMember,
    deleteMember,
    setPrimaryContact,
    isPending:
      state.isCreating || state.isUpdating || state.isDeleting || state.isSettingPrimary,
    ...state,
    // Expose raw mutations for advanced usage
    createMutation,
    updateMutation,
    deleteMutation,
    setPrimaryMutation,
  }
}

// Re-export types for convenience
export type { FamilyMember, CreateFamilyMemberInput, UpdateFamilyMemberInput, RelationshipType }
